import React, { memo, useCallback, useEffect, useRef } from "react";
import { usePlausible } from "../../hooks/usePlausible";
import {
  JOIN_PREMIUM,
  JOIN_PREMIUM_SECTION,
  PRICING_PLAN_VIEW,
  trackButtonClick,
} from "../../utils/analytics";
import styles from "./PremiumSection.module.css";
import { withAssetPath } from "../../utils/basePath";

// Constants for better maintainability
const PRICING_DATA = {
  standard: {
    title: "Standard",
    price: "Free",
    description: "Listen together or listen alone",
    features: [
      "High Audio Quality (160kbps)",
      "Full Music Catalog",
      "Import Playlists",
      "Unlimited Autoplay",
      "Access to All Platforms",
      "Seamless Crossfade",
      "20 Track Queue Length",
      "Limited Audio Interruptions",
    ],
  },
  premium: {
    title: "Premium",
    price: "$4.99 Per Month",
    description:
      "The most powerful music bot, and premium shared with all your friends.",
    features: [
      "Ad-free Unlimited Listening",
      "Premium for All Platforms",
      "Higher Audio Quality (320kbps)",
      "Audio Effects Studio",
      "Unlimited Queue Length",
      "Shared Premium Sessions",
      "Support Artists& Our Team"      
    ],
  },
} as const;

const PLATFORM_LOGOS = [
  { src: withAssetPath("/assets/images/f1.png"), alt: "Discord" },
  { src: withAssetPath("/assets/images/f2.png"), alt: "Spotify" },
  { src: withAssetPath("/assets/images/f3.png"), alt: "Apple Music" },
  { src: withAssetPath("/assets/images/f4.png"), alt: "YouTube Music" },
  { src: withAssetPath("/assets/images/f5.png"), alt: "SoundCloud" },
  { src: withAssetPath("/assets/images/f6.png"), alt: "Twitch" },
] as const;

// Types for better type safety
interface PlanFeatureProps {
  feature: string;
  iconSrc: string;
  iconClass: string;
}

interface PlanCardProps {
  type: "standard" | "premium";
  onPrimaryAction?: () => void;
}

// Memoized Feature Component
const PlanFeature: React.FC<PlanFeatureProps> = memo(
  ({ feature, iconSrc, iconClass }) => (
    <div className={styles.feature}>
      <img src={iconSrc} alt="" className={iconClass} />
      <span>{feature}</span>
    </div>
  )
);

PlanFeature.displayName = "PlanFeature";

// Memoized Platform Logos Component
const PlatformLogos: React.FC = memo(() => (
  <div className={styles.premiumLogos}>
    {PLATFORM_LOGOS.map((logo, index) => (
      <img
        key={index}
        src={logo.src}
        alt={logo.alt}
        className={styles.platformLogo}
        loading="lazy"
      />
    ))}
  </div>
));

PlatformLogos.displayName = "PlatformLogos";

// Main Plan Card Component
const PlanCard: React.FC<PlanCardProps> = memo(({ type, onPrimaryAction }) => {
  const planData = PRICING_DATA[type];
  const isPremium = type === "premium";
  const iconSrc = isPremium
    ? withAssetPath("/assets/images/nav-checkmark.svg")
    : withAssetPath("/assets/images/nav-bullet.svg");
  const iconClass = isPremium ? styles.checkmarkIcon : styles.bulletIcon;

  return (
    <div className={styles.planCard} data-plan={type}>
      <div className={styles.planHeader}>
        <div className={styles.planInfo}>
          <h3 className={styles.planTitle}>{planData.title}</h3>
          <p className={styles.planPrice}>{planData.price}</p>
        </div>
        {isPremium && <PlatformLogos />}
      </div>

      <p className={styles.planDescription}>{planData.description}</p>

      <div className={styles.featuresContainer}>
        <div className={styles.featuresList}>
          {planData.features.map((feature) => (
            <PlanFeature
              key={feature}
              feature={feature}
              iconSrc={iconSrc}
              iconClass={iconClass}
            />
          ))}
        </div>
      </div>

      {isPremium && (
        <div className={styles.actionButtons}>
          <button
            className={styles.primaryButton}
            onClick={onPrimaryAction}
            aria-label="Join Premium"
          >
            Join Premium
          </button>
        </div>
      )}
    </div>
  );
});

PlanCard.displayName = "PlanCard";

// Main Component
const PremiumSection: React.FC = memo(() => {
  const trackEvent = usePlausible();
  const sectionRef = useRef<HTMLDivElement>(null);

  // Event handlers
  const handlePremiumTrial = useCallback(() => {
    trackButtonClick(trackEvent, JOIN_PREMIUM, JOIN_PREMIUM_SECTION, () => {
      window.open(
        "https://premium.rythm.fm/",
        "_blank",
        "noopener,noreferrer"
      );
    });
  }, [trackEvent]);

  // Track when pricing section comes into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            trackEvent(PRICING_PLAN_VIEW);
            observer.unobserve(entry.target); // Only track once
          }
        });
      },
      { threshold: 0.5 } // Trigger when 50% of the section is visible
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [trackEvent]);

  return (
    <section
      ref={sectionRef}
      className={styles.planContainer}
      aria-label="Pricing plans"
    >
      <PlanCard type="standard" />
      <PlanCard type="premium" onPrimaryAction={handlePremiumTrial} />
    </section>
  );
});

PremiumSection.displayName = "PremiumSection";

export default PremiumSection;
